// src/components/ProdMgrs/ProdMgrsPage.jsx
// =============================================================================
// BLOCK PMGR.PAGE.CLONE.0 — ProdMgrs page (clone of Commissions page)
// Notes:
//  - Commissions is NOT modified.
//  - This page mirrors Commissions.jsx layout/behavior as closely as possible.
//  - Differences:
//      * Title: "Program Manager Comm"
//      * Rep dropdown + data endpoints are ProdMgrs-specific
//      * "Product Category" label handled inside ProdMgrs line-table/header
// =============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import PermissionsContext from '../../contexts/PermissionsContext.jsx';
import PermissionsErrorPage from '../PermissionsErrorPage.jsx';
import ProdMgrsCommissionsTable from './components/ProdMgrsCommissionsTable.jsx';
import ProdMgrsMonthlyCommissionsTable from './components/ProdMgrsMonthlyCommissionsTable.jsx';
import ProdMgrsMonthlyNetSalesTable from './components/ProdMgrsMonthlyNetSalesTable.jsx';
import ProdMgrsQuotaBreakdownTable from './components/ProdMgrsQuotaBreakdownTable.jsx';
import ProdMgrsSelectors from './components/ProdMgrsSelectors.jsx';
import ProdMgrsYearOverYearTable from './components/ProdMgrsYearOverYearTable.jsx';

function ProdMgrsPage() {
  const { permissions } = React.useContext(PermissionsContext);

  const [selectedMonth, setSelectedMonth] = React.useState();
  const [selectedRep, setSelectedRep] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());

  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  React.useEffect(() => {
    document.title = 'Program Manager Comm';
  }, []);

  // Match Commissions gating semantics (rep must be selected)
  const isRepSelected = React.useMemo(() => Boolean(selectedRep), [selectedRep]);

  // Keep this consistent with your existing ProdMgrs permissions behavior
  if (
    !permissions.includes('.') &&
    !permissions.includes('commissions') &&
    !permissions.includes('prod-mgrs')
  ) {
    return <PermissionsErrorPage appName="Program Manager Comm" />;
  }

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem' }}>
      <Stack direction="row" spacing={2}>
        <Typography variant="h4" data-cy="prod-mgrs-title">
          Program Manager Comm
        </Typography>

        <ProdMgrsSelectors
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          selectedRep={selectedRep}
          onRepChange={setSelectedRep}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
        />
      </Stack>

      {isRepSelected && (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Grid size={6}>
            <ProdMgrsQuotaBreakdownTable selectedRep={selectedRep} selectedYear={selectedYear} />
          </Grid>

          {/* Match Commissions behavior: show YoY only for current year (and when rep chosen) */}
          {selectedYear === currentYear && (
            <Grid size={6}>
              <ProdMgrsYearOverYearTable selectedRep={selectedRep} selectedYear={selectedYear} />
            </Grid>
          )}

          <Grid size={6}>
            <ProdMgrsMonthlyCommissionsTable
              selectedRep={selectedRep}
              selectedYear={selectedYear}
            />
          </Grid>

          <Grid size={6}>
            <ProdMgrsMonthlyNetSalesTable selectedRep={selectedRep} selectedYear={selectedYear} />
          </Grid>
        </Grid>
      )}

      {selectedRep ? (
        <Box sx={{ mt: 3 }}>
          <ProdMgrsCommissionsTable
            selectedRep={selectedRep}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        </Box>
      ) : (
        <Typography
          variant="h6"
          align="center"
          sx={{ mt: 4, color: 'text.secondary' }}
          data-cy="select-rep-message"
        >
          Please select a rep above
        </Typography>
      )}
    </Box>
  );
}

export default React.memo(ProdMgrsPage);
